import { useTranslation } from 'react-i18next';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Lock, Sparkles, CheckCircle2 } from 'lucide-react';
import { detectUserCurrency, CURRENCY_SYMBOLS, PRICE_AMOUNTS } from '@/lib/stripe-prices';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface PremiumUpsellProps {
  context: 'quiz' | 'result';
  profileName?: string;
}

export const PremiumUpsell = ({ context, profileName }: PremiumUpsellProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const currency = detectUserCurrency();

  const handleUpgrade = () => {
    if (!user) {
      navigate('/auth');
      return;
    }
    navigate('/camera');
  };

  const benefits = context === 'quiz' 
    ? [
        t('quiz.premium.benefit1', { profile: profileName }),
        t('quiz.premium.benefit2'),
        t('quiz.premium.benefit3'),
        t('quiz.premium.benefit4')
      ]
    : [
        t('result.premium.benefit1'),
        t('result.premium.benefit2'),
        t('result.premium.benefit3'),
        t('result.premium.benefit4')
      ];

  return (
    <Card className="p-6 bg-gradient-to-br from-primary/5 to-background border-2 border-primary/30">
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Lock className="w-5 h-5 text-primary" />
          <h3 className="font-semibold text-lg text-primary">
            {context === 'quiz' ? t('quiz.premium.title') : t('result.premium.title')}
          </h3>
        </div>

        <div className="space-y-2">
          {benefits.map((benefit, index) => (
            <div key={index} className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
              <p className="text-sm text-muted-foreground">{benefit}</p>
            </div>
          ))}
        </div>

        <Button
          onClick={handleUpgrade}
          size="lg"
          className="w-full h-12 bg-gradient-to-r from-primary to-primary/80 hover:opacity-90"
        >
          <Sparkles className="mr-2 h-5 w-5" />
          {t('quiz.premium.cta')} - {CURRENCY_SYMBOLS[currency]}{PRICE_AMOUNTS[currency].toFixed(2)}
        </Button>

        <p className="text-xs text-center text-muted-foreground">
          ({t('quiz.premium.evaluations', { count: 77 })})
        </p>

        <p className="text-xs text-center text-muted-foreground">
          {t('quiz.premium.subtitle')}
        </p>
      </div>
    </Card>
  );
};
